import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkOperationsPanel = ({ selectedCampaigns = [], onOperationComplete, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState('');
  const [operationParams, setOperationParams] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const operationOptions = [
    { value: 'pause', label: 'Pause Campaigns', icon: 'Pause' },
    { value: 'resume', label: 'Resume Campaigns', icon: 'Play' },
    { value: 'duplicate', label: 'Duplicate Campaigns', icon: 'Copy' },
    { value: 'archive', label: 'Archive Campaigns', icon: 'Archive' },
    { value: 'export', label: 'Export Data', icon: 'Download' },
    { value: 'update-budget', label: 'Update Budget', icon: 'DollarSign' },
    { value: 'extend-dates', label: 'Extend End Dates', icon: 'Calendar' },
    { value: 'add-tags', label: 'Add Tags', icon: 'Tag' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' }
  ];

  const exportFormatOptions = [
    { value: 'csv', label: 'CSV Format' },
    { value: 'xlsx', label: 'Excel Format' },
    { value: 'pdf', label: 'PDF Report' },
    { value: 'json', label: 'JSON Data' }
  ];

  const handleOperationChange = (operation) => {
    setSelectedOperation(operation);
    setOperationParams({});
  };

  const handleExecuteOperation = async () => {
    if (!selectedOperation || selectedCampaigns?.length === 0) return;

    setIsProcessing(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Here you would implement the actual bulk operation logic
      console.log('Executing bulk operation:', {
        operation: selectedOperation,
        campaigns: selectedCampaigns,
        params: operationParams
      });

      if (onOperationComplete) {
        onOperationComplete({
          operation: selectedOperation,
          campaigns: selectedCampaigns,
          success: true
        });
      }

      // Reset form
      setSelectedOperation('');
      setOperationParams({});
      setIsOpen(false);

    } catch (error) {
      console.error('Bulk operation failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedCampaigns?.length === 0) return;

    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log(`Executing ${action} on campaigns:`, selectedCampaigns);
      
      if (onOperationComplete) {
        onOperationComplete({
          operation: action,
          campaigns: selectedCampaigns,
          success: true
        });
      }
    } catch (error) {
      console.error(`${action} operation failed:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderOperationParams = () => {
    switch (selectedOperation) {
      case 'update-budget':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">New Budget (€)</label>
              <input
                type="number"
                value={operationParams?.budget || ''}
                onChange={(e) => setOperationParams({ ...operationParams, budget: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Enter new budget amount"
              />
            </div>
            <Checkbox
              label="Apply proportionally based on current spend"
              checked={operationParams?.proportional || false}
              onChange={(e) => setOperationParams({ ...operationParams, proportional: e?.target?.checked })}
            />
          </div>
        );

      case 'extend-dates':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Extend by (days)</label>
              <input
                type="number"
                value={operationParams?.days || ''}
                onChange={(e) => setOperationParams({ ...operationParams, days: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Number of days to extend"
              />
            </div>
            <Checkbox
              label="Only extend active campaigns"
              checked={operationParams?.activeOnly || false}
              onChange={(e) => setOperationParams({ ...operationParams, activeOnly: e?.target?.checked })}
            />
          </div>
        );

      case 'add-tags':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Tags (comma-separated)</label>
              <input
                type="text"
                value={operationParams?.tags || ''}
                onChange={(e) => setOperationParams({ ...operationParams, tags: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="e.g., seasonal, high-priority, mobile"
              />
            </div>
          </div>
        );

      case 'duplicate':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Name Suffix</label>
              <input
                type="text"
                value={operationParams?.suffix || ''}
                onChange={(e) => setOperationParams({ ...operationParams, suffix: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="e.g., - Copy, - Q4 2024"
              />
            </div>
            <Select
              label="Initial Status"
              options={statusOptions}
              value={operationParams?.status || 'paused'}
              onChange={(value) => setOperationParams({ ...operationParams, status: value })}
            />
          </div>
        );

      case 'export':
        return (
          <div className="space-y-4">
            <Select
              label="Export Format"
              options={exportFormatOptions}
              value={operationParams?.format || 'csv'}
              onChange={(value) => setOperationParams({ ...operationParams, format: value })}
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">Include Data</label>
              <div className="space-y-2">
                <Checkbox
                  label="Performance metrics"
                  checked={operationParams?.includeMetrics !== false}
                  onChange={(e) => setOperationParams({ ...operationParams, includeMetrics: e?.target?.checked })}
                />
                <Checkbox
                  label="QR code details"
                  checked={operationParams?.includeQRs !== false}
                  onChange={(e) => setOperationParams({ ...operationParams, includeQRs: e?.target?.checked })}
                />
                <Checkbox
                  label="Historical data"
                  checked={operationParams?.includeHistory || false}
                  onChange={(e) => setOperationParams({ ...operationParams, includeHistory: e?.target?.checked })}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getOperationIcon = (operation) => {
    const option = operationOptions?.find(opt => opt?.value === operation);
    return option ? option?.icon : 'Settings';
  };

  const getOperationDescription = (operation) => {
    switch (operation) {
      case 'pause': return 'Temporarily stop all selected campaigns';
      case 'resume': return 'Reactivate paused campaigns';
      case 'duplicate': return 'Create copies of selected campaigns';
      case 'archive': return 'Move campaigns to archive (cannot be undone)';
      case 'export': return 'Download campaign data in selected format';
      case 'update-budget': return 'Modify budget allocation for campaigns';
      case 'extend-dates': return 'Extend campaign end dates';
      case 'add-tags': return 'Add organizational tags to campaigns';
      default: return '';
    }
  };

  if (selectedCampaigns?.length === 0) {
    return null;
  }

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Settings" size={20} className="text-primary" />
            <h3 className="font-semibold text-foreground">Bulk Operations</h3>
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              {selectedCampaigns?.length} selected
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} />
          </Button>
        </div>
      </div>
      {/* Content */}
      {isOpen && (
        <div className="p-4 space-y-4">
          {/* Operation Selection */}
          <Select
            label="Select Operation"
            options={operationOptions}
            value={selectedOperation}
            onChange={handleOperationChange}
            placeholder="Choose an operation..."
          />

          {/* Operation Description */}
          {selectedOperation && (
            <div className="p-3 bg-muted/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={getOperationIcon(selectedOperation)} size={16} className="text-primary" />
                <span className="font-medium text-foreground">
                  {operationOptions?.find(opt => opt?.value === selectedOperation)?.label}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {getOperationDescription(selectedOperation)}
              </p>
            </div>
          )}

          {/* Operation Parameters */}
          {selectedOperation && renderOperationParams()}

          {/* Selected Campaigns Preview */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Selected Campaigns ({selectedCampaigns?.length})
            </label>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {selectedCampaigns?.map((campaignId) => (
                <div key={campaignId} className="flex items-center gap-2 p-2 bg-muted/20 rounded text-sm">
                  <Icon name="Target" size={14} className="text-muted-foreground" />
                  <span className="text-foreground">{campaignId}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bulk Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => handleBulkAction('activate')}
              disabled={isLoading}
              size="sm"
              className="bg-green-600 text-white hover:bg-green-700"
            >
              <Icon name="Play" size={14} className="mr-1" />
              Activate
            </Button>

            <Button
              onClick={() => handleBulkAction('pause')}
              disabled={isLoading}
              size="sm"
              className="bg-yellow-600 text-white hover:bg-yellow-700"
            >
              <Icon name="Pause" size={14} className="mr-1" />
              Pause
            </Button>

            <Button
              onClick={() => handleBulkAction('complete')}
              disabled={isLoading}
              size="sm"
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              <Icon name="CheckCircle" size={14} className="mr-1" />
              Complete
            </Button>

            <Button
              onClick={() => handleBulkAction('archive')}
              disabled={isLoading}
              size="sm"
              variant="outline"
              className="border-gray-300"
            >
              <Icon name="Archive" size={14} className="mr-1" />
              Archive
            </Button>

            <Button
              onClick={() => handleBulkAction('delete')}
              disabled={isLoading}
              size="sm"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <Icon name="Trash2" size={14} className="mr-1" />
              Delete
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="default"
              onClick={handleExecuteOperation}
              disabled={!selectedOperation || isProcessing}
              loading={isProcessing}
              iconName={isProcessing ? 'Loader2' : getOperationIcon(selectedOperation)}
              iconPosition="left"
              iconSize={16}
              className="flex-1"
            >
              {isProcessing ? 'Processing...' : 'Execute Operation'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedOperation('');
                setOperationParams({});
                setIsOpen(false);
              }}
              disabled={isProcessing}
            >
              Cancel
            </Button>
          </div>

          {/* Warning for destructive operations */}
          {['archive', 'delete']?.includes(selectedOperation) && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="AlertTriangle" size={16} className="text-error" />
                <span className="font-medium text-error">Warning</span>
              </div>
              <p className="text-sm text-error">
                This operation cannot be undone. Please confirm you want to proceed.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkOperationsPanel;